/**
 * Cloudflare Turnstile widget (explicit render).
 * The script is loaded globally from index.html.
 */
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

const RETRY_MS = 300;

export default function TurnstileWidget({ onVerify, theme = 'light', size = 'normal' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [siteKey, setSiteKey] = useState(null);

  // Fetch site key from the backend (keeps env-driven config on the server).
  useEffect(() => {
    let mounted = true;
    api
      .get('/api/public/turnstile-key')
      .then((res) => mounted && setSiteKey(res.data.site_key))
      .catch(() => mounted && setSiteKey('1x00000000000000000000AA'));
    return () => {
      mounted = false;
    };
  }, []);

  // Render the widget once Turnstile is available.
  useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let cancelled = false;

    const render = () => {
      if (cancelled) return;
      const ts = window.turnstile;
      if (!ts) {
        setTimeout(render, RETRY_MS);
        return;
      }
      try {
        if (widgetIdRef.current) {
          ts.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
        widgetIdRef.current = ts.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          callback: (token) => onVerify?.(token),
          'error-callback': () => onVerify?.(null),
          'expired-callback': () => onVerify?.(null),
          'timeout-callback': () => onVerify?.(null),
        });
      } catch (_) {
        // ignore — widget may already be rendered
      }
    };

    render();

    return () => {
      cancelled = true;
      try {
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      } catch (_) {
        /* noop */
      }
    };
  }, [siteKey, theme, size, onVerify]);

  return <div ref={containerRef} data-testid="turnstile-widget" />;
}
